import { useSelector, useDispatch } from "react-redux";
import { toggleSidebar } from "../redux/uiSlice";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Store,
  Settings,
  Menu,
  MessageSquare,
  User,
  Wallet,
  Truck,
  Lightbulb,
  Sparkles,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Activity,
  Bell,
  HelpCircle,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const Sidebar = () => {
  const dispatch = useDispatch();
  const sidebarOpen = useSelector((state) => state.ui.sidebarOpen);
  const location = useLocation();
  const { theme } = useTheme();

  const menuItems = [
    { path: "/dashboard", label: "Dashboard", icon: <Home size={22} /> },
    { path: "/products", label: "Products", icon: <Store size={22} /> },
    { path: "/__profile", label: "My Profile", icon: <User size={22} /> },
    { path: "/your_wallet", label: "Wallet", icon: <Wallet size={22} /> },
    { path: "/shipwithue", label: "Ship With UE", icon: <Truck size={22} /> },
    { path: "/suggestion", label: "Products Reviews", icon: <Lightbulb size={22} /> },
    { path: "/__settings_", label: "Settings", icon: <Settings size={22} /> },
  ];

  const secondaryItems = [
    { path: "/help", label: "Help & Support", icon: <HelpCircle size={20} /> },
    { path: "/logout", label: "Logout", icon: <LogOut size={20} /> },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <motion.aside
      initial={false}
      animate={{ width: sidebarOpen ? 280 : 80 }}
      className={`
        fixed md:static
        top-0 left-0
        z-50
        h-screen
        bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900
        shadow-2xl
        transition-all duration-300 ease-in-out
        flex flex-col
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-white/10">
        <AnimatePresence mode="wait">
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-white text-lg">Unique Elevator</h2>
                <p className="text-xs text-gray-400">Admin Panel</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => dispatch(toggleSidebar())}
          className={`
            hidden md:flex
            w-10 h-10 rounded-xl
            items-center justify-center
            text-white/70 hover:text-white
            hover:bg-white/10
            transition-all
          `}
        >
          {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </motion.button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto">
        {/* Main Menu */}
        <div className="px-3 mb-6">
          <AnimatePresence mode="wait">
            {sidebarOpen && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-3"
              >
                Main Menu
              </motion.p>
            )}
          </AnimatePresence>

          <ul className="space-y-2">
            {menuItems.map((item, index) => (
              <motion.li key={item.path} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}>
                <Link
                  to={item.path}
                  className={`
                    flex items-center gap-3
                    px-4 py-3 rounded-xl
                    transition-all duration-200
                    group relative
                    ${isActive(item.path)
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
                      : "text-gray-300 hover:bg-white/10 hover:text-white"
                    }
                  `}
                >
                  {/* Active Indicator */}
                  {isActive(item.path) && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full shadow-lg"
                    />
                  )}

                  {/* Icon */}
                  <div className={`relative z-10 ${isActive(item.path) ? "text-white" : "text-gray-400 group-hover:text-white"}`}>
                    {item.icon}
                  </div>

                  {/* Label */}
                  <AnimatePresence mode="wait">
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: "auto" }}
                        exit={{ opacity: 0, width: 0 }}
                        className="relative z-10 font-medium whitespace-nowrap overflow-hidden"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {/* Hover Effect */}
                  {!isActive(item.path) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Secondary Menu */}
        <div className="px-3">
          <AnimatePresence mode="wait">
            {sidebarOpen && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-3"
              >
                Support
              </motion.p>
            )}
          </AnimatePresence>

          <ul className="space-y-2">
            {secondaryItems.map((item, index) => (
              <motion.li key={item.path} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: (menuItems.length + index) * 0.05 }}>
                <Link
                  to={item.path}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-white/10 hover:text-white transition-all duration-200 group"
                >
                  <div className="text-gray-500 group-hover:text-white transition-colors">
                    {item.icon}
                  </div>
                  <AnimatePresence mode="wait">
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: "auto" }}
                        exit={{ opacity: 0, width: 0 }}
                        className="font-medium whitespace-nowrap overflow-hidden"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-white/10">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className={`
            flex items-center gap-3
            p-3 rounded-xl
            bg-white/5 hover:bg-white/10
            cursor-pointer
            transition-all
          `}
        >
          <img
            src="https://i.pravatar.cc/40"
            alt="User"
            className="w-10 h-10 rounded-xl object-cover ring-2 ring-white/20"
          />
          {/* <AnimatePresence mode="wait">
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="flex-1 overflow-hidden"
              >
                <p className="font-semibold text-white text-sm">Admin User</p>
                <p className="text-xs text-gray-400">admin@ue.com</p>
              </motion.div>
            )}
          </AnimatePresence> */}
          {sidebarOpen && (
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-gray-400">Online</span>
            </div>
          )}
        </motion.div>
      </div>

      {/* Collapse Button (Mobile) */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => dispatch(toggleSidebar())}
        className="md:hidden absolute top-4 -right-3 w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center text-white text-xs shadow-lg"
      >
        <Menu size={14} />
      </motion.button>
    </motion.aside>
  );
};

export default Sidebar;
